import { RequestPhoneVerificationPinResponse } from '@shared/api';

import { createMachine } from 'xstate';

import {
  MobileCodeLoanApplicationData,
  LoanApplicationStepId,
  LoanApplicationData,
} from './types/LoanApplication.types';
import { GettingStartedStep } from '../components/Application/GettingStartedStep';
import { HowMuchWouldYouLikeToBorrowStep } from '../components/Application/HowMuchWouldYouLikeToBorrowStep';
import { NominateBankAccountStep } from '../components/Application/NominateBankAccountStep';
import { ReviewLoanRequestStep } from '../components/Application/ReviewLoanRequestStep';
import { SignLoanAgreementStep } from '../components/Application/SignLoanAgreementStep';
import { SubmittedStep } from '../components/Application/SubmittedStep';
import { TellUsAboutYouStep } from '../components/Application/TellUsAboutYouStep';
import { TellUsAboutYourFinancialsStep } from '../components/Application/TellUsAboutYourFinancialsStep';
import { WhatIsYourLoanForStep } from '../components/Application/WhatIsYourLoanForStep';

export interface Context {
  applicationData: Partial<LoanApplicationData>; // Added field to store application data
  completedSteps: Partial<Record<LoanApplicationStepId, boolean>>; // Map of IDs of completed steps
  mobileCodeLoanApplicationData: MobileCodeLoanApplicationData;
}

export type Event =
  | { type: 'NEXT'; data?: Partial<LoanApplicationData> }
  | { type: 'SUBMIT'; data?: Partial<LoanApplicationData> }
  | { type: 'BACK' }
  | { type: 'COMPLETE_STEP'; stepId: string }
  | { type: 'SAVE_AND_EXIT' }
  | { type: 'SKIP' }
  | { type: 'MOBILE_CODE_INPUT'; code: string }
  | { type: 'MOBILE_CODE_RESENT' }
  | { type: 'EDIT_LOAN_SUMMARY' }
  | { type: 'EDIT_ABOUT_YOU' }
  | { type: 'EDIT_FINANCIALS' };

export type Services = {
  fetchLoanApplicationData: {
    data: Partial<LoanApplicationData>;
  };
  sendMobileCode: {
    data: RequestPhoneVerificationPinResponse;
  };
  verifyMobileCode: {
    data: void;
  };
  syncDataWithDB: {
    data: void; // TODO: at the moment the sync is one-way, the FE does not care about the response
  };
};

export const loanApplicationMachine = createMachine({
  context: {
    applicationData: {}, // Initialize applicationData
    completedSteps: {},
    mobileCodeLoanApplicationData: { userInputCode: '', error: '' },
  },
  tsTypes: {} as import('./LoanApplication.machine.typegen').Typegen0,
  schema: { context: {} as Context, events: {} as Event, services: {} as Services },
  predictableActionArguments: true,
  id: 'loanApplication',
  initial: 'loading',
  states: {
    loading: {
      initial: 'fetchLoanApplicationData',
      states: {
        fetchLoanApplicationData: {
          id: 'fetchLoanApplicationData',
          invoke: {
            src: 'fetchLoanApplicationData',
            onDone: {
              target: 'findStartingStep',
              actions: ['prefillLoanApplicationData'], // This action will populate context.applicationData
            },
            onError: {
              target: 'findStartingStep',
            },
          },
        },
        findStartingStep: {
          always: [
            {
              target: '#gettingStarted',
            },
          ],
        },
      },
    },

    gettingStarted: {
      id: 'gettingStarted',
      on: {
        NEXT: {
          target: '#tailorYourLoan.loanAmount',
          actions: ['assignLoanApplicationData'],
        },
      },
      meta: {
        step: true,
        Component: GettingStartedStep,
      },
    },

    // TODO:
    // jumpToPosition: {
    //   id: 'jumpToPosition',
    //   always: [
    //     {
    //       target: '#tailorYourLoan',
    //       cond: 'hasNotCompletedTailorYourLoanStep',
    //     },
    //     {
    //       target: '#submitted',
    //     },
    //   ],
    // },

    tailorYourLoan: {
      id: 'tailorYourLoan',
      initial: 'loanAmount',
      meta: {
        step: true,
      },
      states: {
        // TODO: check if loanType required
        // TODO: check if loanPurpose required
        loanAmount: {
          meta: {
            step: true,
            hidden: true,
            Component: HowMuchWouldYouLikeToBorrowStep,
          },
          on: {
            BACK: {
              target: '#gettingStarted',
            },
            NEXT: {
              target: '#tailorYourLoan.purposeOfLoan',
              actions: ['assignLoanApplicationData', 'syncDataWithDB'],
            },
          },
        },
        purposeOfLoan: {
          meta: {
            step: true,
            hidden: true,
            Component: WhatIsYourLoanForStep,
          },
          on: {
            BACK: {
              target: '#tailorYourLoan.loanAmount',
            },
            NEXT: {
              target: '#tellUsAboutYou',
              actions: ['assignLoanApplicationData', 'syncDataWithDB'],
            },
          },
        },
      },
    },

    tellUsAboutYou: {
      id: 'tellUsAboutYou',
      meta: {
        step: true,
        Component: TellUsAboutYouStep,
      },
      on: {
        BACK: {
          target: '#tailorYourLoan.purposeOfLoan',
        },
        NEXT: {
          target: '#incomeAndExpenses',
          actions: ['assignLoanApplicationData', 'syncDataWithDB'],
        },
      },
    },

    incomeAndExpenses: {
      id: 'incomeAndExpenses',
      meta: {
        step: true,
        Component: TellUsAboutYourFinancialsStep,
      },
      on: {
        BACK: {
          target: '#tellUsAboutYou',
        },
        NEXT: {
          target: '#nominateBankAccount',
          actions: ['assignLoanApplicationData', 'syncDataWithDB'],
        },
      },
    },

    nominateBankAccount: {
      id: 'nominateBankAccount',
      meta: {
        step: true,
        Component: NominateBankAccountStep,
        nextButtonLabel: 'I have deposited',
      },
      on: {
        BACK: {
          target: '#incomeAndExpenses',
        },
        NEXT: {
          target: '#reviewAndSubmit.reviewLoanRequest',
          actions: ['assignLoanApplicationData', 'syncDataWithDB'],
        },
      },
    },

    reviewAndSubmit: {
      id: 'reviewAndSubmit',
      initial: 'reviewLoanRequest',
      meta: {
        step: true,
      },
      states: {
        reviewLoanRequest: {
          meta: {
            step: true,
            hidden: true,
            Component: ReviewLoanRequestStep,
          },
          on: {
            BACK: {
              target: '#nominateBankAccount',
            },
            NEXT: {
              target: '#reviewAndSubmit.signLoanAgreement',
              actions: ['assignLoanApplicationData', 'syncDataWithDB'],
            },
            EDIT_LOAN_SUMMARY: {
              target: '#tailorYourLoan.loanAmount',
            },
            EDIT_ABOUT_YOU: {
              target: '#tellUsAboutYou',
            },
            EDIT_FINANCIALS: {
              target: '#incomeAndExpenses',
            },
          },
        },
        signLoanAgreement: {
          meta: {
            step: true,
            hidden: true,
            Component: SignLoanAgreementStep,
            nextButtonLabel: 'Submit application',
          },
          on: {
            BACK: {
              target: '#reviewAndSubmit.reviewLoanRequest',
            },
            NEXT: {
              target: '#submitted',
              actions: ['assignLoanApplicationData', 'syncDataWithDB'],
            },
          },
        },
      },
    },

    // TODO: SMS OTP

    submitted: {
      id: 'submitted',
      meta: {
        step: false,
        hidden: true,
        Component: SubmittedStep,
      },
    },
  },
  on: {
    COMPLETE_STEP: {
      actions: ['completeStep'],
    },
    SAVE_AND_EXIT: {
      actions: ['redirectToDashboard'],
    },
  },
});
